import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, Mail, MapPin, Cpu, Layers, Palette, ArrowRight } from "lucide-react";
import { SiFacebook, SiLinkedin, SiYoutube, SiInstagram } from "react-icons/si";
import { IMAGES } from "@/assets/images";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const productionItems = [
    { title: "Döküm",        href: "/tr/uretim#foundry",           icon: <Cpu className="h-5 w-5" />,    desc: "Yüksek kaliteli alüminyum biyet üretimi." },
    { title: "Kalıp İmalat", href: "/tr/uretim#mold-manufacturing", icon: <Layers className="h-5 w-5" />, desc: "Ekstrüzyon kalıp tasarım ve imalat hizmetleri." },
    { title: "Ekstrüzyon",   href: "/tr/uretim#extrusion",          icon: <Cpu className="h-5 w-5" />,    desc: "4\" - 8\" geniş ekstrüzyon hattı kapasitesi." },
    { title: "Yüzey İşlem",  href: "/tr/uretim#surface-treatment",  icon: <Palette className="h-5 w-5" />,desc: "Anodizasyon ve elektrostatik toz boya tesisleri." },
    { title: "Mekanik İşlem",href: "/tr/uretim#mechanical",         icon: <Layers className="h-5 w-5" />, desc: "Hassas kesim, delme ve CNC işleme çözümleri." },
  ];

  const productItems = [
    { title: "Mimari Sistemler",      href: "/tr/mimari-sistemler",         desc: "Pencere, kapı ve cephe sistemleri için estetik çözümler." },
    { title: "Endüstriyel Profiller", href: "/tr/endustriyel-profiller",    desc: "Otomotiv, enerji ve makine sektörlerine özel alaşım profiller." },
    { title: "Cephe Sistemleri",      href: "/tr/cephe-dograma-sistemleri", desc: "Modern binalar için estetik cephe kaplama ve doğrama çözümleri." },
  ];

  return (
    <div className="flex min-h-screen flex-col">
      {/* Top Bar */}
      <div className="hidden lg:block py-2 text-primary-foreground bg-[rgb(29,46,105)]">
        <div className="container mx-auto px-4 flex justify-between items-center text-xs">
          <div className="flex gap-6">
            <span className="flex items-center gap-1.5 font-bold text-[rgb(255,255,255)]">
              <Mail className="h-3.5 w-3.5" /> info@mimalum.com
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold xl:text-xs text-[rgb(255,255,255)]">TR</span>
            <span className="opacity-50 text-[10px] font-bold xl:text-xs text-[rgb(255,255,255)]">|</span>
            <Link to="/en" className="text-[10px] font-bold xl:text-xs text-[rgb(255,255,255)] hover:opacity-100 opacity-70 transition-opacity">EN</Link>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b bg-white", isScrolled ? "shadow-md" : "")}>
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/tr" className="flex items-center -ml-4">
            <img src={IMAGES.LOGO_276} alt="Şirket Logosu" className="h-[100px] object-contain" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to="/tr">Ana Sayfa</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to="/tr/kurumsal">Hakkımızda</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger>Ürünler</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid gap-3 p-6 w-[400px]">
                      {productItems.map(item => (
                        <li key={item.href}>
                          <NavigationMenuLink asChild>
                            <Link to={item.href} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                              <div className="text-sm font-semibold leading-none">{item.title}</div>
                              <p className="line-clamp-2 text-xs leading-snug text-muted-foreground">{item.desc}</p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to="/tr/aluminyum-billet">Alüminyum Billet / Ingot</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger>Üretim</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                      {productionItems.map(item => (
                        <li key={item.title}>
                          <NavigationMenuLink asChild>
                            <Link to={item.href} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                              <div className="flex items-center gap-2">
                                {item.icon}
                                <div className="text-sm font-medium leading-none">{item.title}</div>
                              </div>
                              <p className="line-clamp-2 text-xs leading-snug text-muted-foreground mt-2">{item.desc}</p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to="/tr/surdurulebilirlik">Sürdürülebilirlik</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to="/tr/profil-rehberi">Profil Rehberi</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                    <NavLink to="/tr/iletisim">İletişim</NavLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Quote Button */}
          <div className="hidden lg:block">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-2 font-semibold">
              <Link to="/tr/iletisim#quote-form">Teklif Al</Link>
            </Button>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden">
            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px] overflow-y-auto max-h-screen">
                <SheetHeader>
                  <SheetTitle>Menü</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-4 mt-8">
                  <NavLink to="/tr" className="text-lg font-medium" onClick={() => setIsSheetOpen(false)}>Ana Sayfa</NavLink>
                  <NavLink to="/tr/kurumsal" className="text-lg font-medium" onClick={() => setIsSheetOpen(false)}>Hakkımızda</NavLink>

                  <div className="space-y-2">
                    <div className="text-lg font-medium">Ürünler</div>
                    <div className="pl-4 space-y-2">
                      {productItems.map(item => (
                        <Link key={item.href} to={item.href} className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsSheetOpen(false)}>
                          {item.title}
                        </Link>
                      ))}
                    </div>
                  </div>

                  <NavLink to="/tr/aluminyum-billet" className="text-lg font-medium" onClick={() => setIsSheetOpen(false)}>Alüminyum Billet / Ingot</NavLink>

                  <div className="flex flex-col gap-2">
                    <span className="text-sm text-muted-foreground uppercase tracking-widest font-bold">Üretim</span>
                    {productionItems.map(item => (
                      <Link key={item.title} to={item.href} className="pl-4 text-md py-1" onClick={() => setIsSheetOpen(false)}>{item.title}</Link>
                    ))}
                  </div>

                  <NavLink to="/tr/surdurulebilirlik" className="text-lg font-medium" onClick={() => setIsSheetOpen(false)}>Sürdürülebilirlik</NavLink>
                  <NavLink to="/tr/profil-rehberi" className="text-lg font-medium" onClick={() => setIsSheetOpen(false)}>Profil Rehberi</NavLink>
                  <NavLink to="/tr/iletisim" className="text-lg font-medium" onClick={() => setIsSheetOpen(false)}>İletişim</NavLink>

                  <div className="pt-4 border-t">
                    <div className="text-sm font-medium mb-2">Dil</div>
                    <div className="flex gap-2">
                      <span className="px-3 py-1 rounded text-sm bg-primary text-primary-foreground">Türkçe</span>
                      <Link to="/en" className="px-3 py-1 rounded text-sm bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground" onClick={() => setIsSheetOpen(false)}>English</Link>
                    </div>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <div className="space-y-6">
              <Link to="/tr" className="flex items-center">
                <div className="bg-white text-primary p-2 rounded">
                  <img src={IMAGES.LOGO_276} alt="Logo" className="h-12 w-auto object-contain" />
                </div>
              </Link>
              <p className="text-sm text-primary-foreground/70 leading-relaxed">
                2020 yılında kurulan şirketimiz, entegre üretim tesislerimizde yüksek kaliteli alüminyum çözümleri üretiyor.
                Dökümden mekanik işlemeye kadar her aşamada endüstriyel mükemmelliği hedefliyoruz.
              </p>
              <div className="flex gap-4">
                <a href="#" className="p-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full"><SiLinkedin className="h-5 w-5" /></a>
                <a href="https://www.instagram.com/mimalum.aluminum" target="_blank" rel="noopener noreferrer" className="p-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full"><SiInstagram className="h-5 w-5" /></a>
                <a href="#" className="p-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full"><SiFacebook className="h-5 w-5" /></a>
                <a href="#" className="p-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full"><SiYoutube className="h-5 w-5" /></a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-bold mb-6 border-l-4 border-accent pl-3">Hızlı Erişim</h3>
              <ul className="space-y-4 text-sm text-primary-foreground/70">
                <li><Link to="/tr/kurumsal" className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Hakkımızda</Link></li>
                <li><Link to="/tr/uretim" className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Üretim Tesisleri</Link></li>
                <li><Link to="/tr/urunler" className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Ürün Katalogları</Link></li>
                <li><Link to="/tr/surdurulebilirlik" className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Sürdürülebilirlik</Link></li>
                <li><Link to="/tr/indirilebilirler" className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Sertifikalarımız</Link></li>
              </ul>
            </div>

            {/* Production Units */}
            <div>
              <h3 className="text-lg font-bold mb-6 border-l-4 border-accent pl-3">Üretim Birimleri</h3>
              <ul className="space-y-4 text-sm text-primary-foreground/70">
                <li><Link to="/tr/uretim/dokumhane" className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Alüminyum Döküm</Link></li>
                <li><Link to="/tr/uretim/ekstruzyon" className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Profil Ekstrüzyon</Link></li>
                <li><Link to="/tr/uretim/yuzey-islem" className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Yüzey Kaplama</Link></li>
                <li><Link to="/tr/uretim/mekanik-islem" className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Mekanik İşleme</Link></li>
                <li><Link to="/tr/urunler/endustriyel-profiller" className="hover:text-white flex items-center gap-2 transition-colors"><ArrowRight className="h-3 w-3" /> Endüstriyel Çözümler</Link></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <h3 className="text-lg font-bold mb-6 border-l-4 border-accent pl-3">İletişim</h3>
              <div className="space-y-4 text-sm">
                <div className="flex gap-3">
                  <MapPin className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                  <div className="text-primary-foreground/70 space-y-2">
                    <div>
                      <p className="font-semibold text-primary-foreground/90 text-xs uppercase tracking-wider mb-0.5">Merkez Ofis</p>
                      <p>Zuhuratbaba Mah. Gençler Cad. NO:58/1 <br />Bakırköy, İSTANBUL</p>
                    </div>
                    <div>
                      <p className="font-semibold text-primary-foreground/90 text-xs uppercase tracking-wider mb-0.5">Fabrika</p>
                      <p>Pelitli Mah. Tavşanlı Yolu Cad. No:123 <br />Gebze / KOCAELİ</p>
                    </div>
                    <div>
                      <p className="font-semibold text-primary-foreground/90 text-xs uppercase tracking-wider mb-0.5">USA Ofisi</p>
                      <p>361 South Bundy Drive <br />Los Angeles, CA 90049</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Phone className="h-5 w-5 text-accent shrink-0" />
                  <div className="text-primary-foreground/70">
                    <p>İhracat: +90 212 671 28 70</p>
                    <p>Bilgi: +90 532 585 08 50</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Mail className="h-5 w-5 text-accent shrink-0" />
                  <p className="text-primary-foreground/70">info@mimalum.com</p>
                </div>
              </div>
              <Button asChild variant="secondary" className="w-full font-bold">
                <Link to="/tr/iletisim">Mesaj Gönder</Link>
              </Button>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-primary-foreground/50">
            <p>© 2026 Mimhas Intelligence. Tüm Hakları Saklıdır.</p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-white transition-colors">Gizlilik Politikası</a>
              <a href="#" className="hover:text-white transition-colors">Kullanım Koşulları</a>
              <a href="#" className="hover:text-white transition-colors">Veri Koruma</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
